import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

import {FormComponent} from 'appShared/widgets';
import {FormInterface} from 'appShared/interfaces';
import {
    DialogActionsEnum,
    DialogButtonInterface,
    DialogButtonTypesEnum,
    DialogConfigInterface,
    SelectOptionInterface
} from 'appShared/components';

import {Router} from '@angular/router';

import {PanelPaths} from 'appShared/paths';
import {AlertsService} from 'appShared/services';
import {Subscription} from 'rxjs';
import {AbstractControl, Validators} from '@angular/forms';
import {FundOrderMessages} from 'appAssets/messages';
import {Helper} from 'appShared/classes';

import {FundOrderBuyForm} from 'appEntries/panel/forms';

import {
    AccountBalanceResponseInterface,
    AccountSubTypeEnum,
    CashAccountDetailedReportModel,
    CurrencyTypesEnum,
    FundOrderBaseInterface,
    FundOrderPlacementMethodEnum,
    FundOrdersService,
    FundOrderTypesEnum,
    FundProductInterface,
    OperatorInterface,
    ReportExtensions,
    TradingAccountDetailedReportModel
} from '@spa/api';

import {
    FundCreateOrderComponent,
    FundOrderBuyAmountSelector,
    FundOrderBuyProductSelector,
    GetFundOrderBuyAmountPlaceholderText,
    GetFundOrderBuyPurchaseDateText
} from 'appAssets/descriptors';

@Component({
    selector: 'app-fund-order-buy',
    templateUrl: './fund-order-buy.component.html',
    styleUrls: ['./fund-order-buy.component.scss']
})
export class FundOrderBuyComponent extends FundCreateOrderComponent<FundOrderBuyComponent> implements OnInit, OnDestroy, FormInterface {
    private readonly panelPaths: typeof PanelPaths;
    private readonly products: FundProductInterface[];
    public readonly operator: OperatorInterface;

    private amountControl: AbstractControl;
    private productControl: AbstractControl;

    public readonly isMutualFundAccount: boolean;

    public orderForm: FundOrderBuyForm;
    public account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel;

    public calculatedAmount: string;
    public accountCashCurrencySign: string;
    public productCurrencySign: string;
    public availableCashText: string;
    public showProductPriceSection: boolean;
    public productPriceBoxText: string;
    public productPriceBoxWarningText: string;
    public productPriceDate: string;
    public productPriceAmount: number;
    public amountNotValidBoxText: string;

    constructor(
        public readonly ordersService: FundOrdersService,
        public readonly router: Router,
        public readonly alertsService: AlertsService,
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            account: TradingAccountDetailedReportModel | CashAccountDetailedReportModel,
            products: FundProductInterface[],
            operator: OperatorInterface,
            accountBalance: AccountBalanceResponseInterface
        },
        public dialogRef: MatDialogRef<FundOrderBuyComponent>,
    ) {
        super(data.account, data.operator, ordersService, alertsService, router, dialogRef);

        this.orderType = FundOrderTypesEnum.Buy;

        this.config = data.config;
        this.account = data.account;
        this.products = data.products.filter(p => p.buyPrice);
        this.operator = data.operator;
        this.panelPaths = PanelPaths;

        this.isMutualFundAccount = this.data.account
            ? this.data.account.AccountSubType === AccountSubTypeEnum.MutualFund : false;

        this.calculatedAmount = '0';
        this.showProductPriceSection = false;
        this.productPriceAmount = 0;
        this.amountNotValidBoxText = '';
        this.model.cash = data.accountBalance;
    }

    private getInputModel(): FundOrderBaseInterface | null {
        const productCode = this.orderForm.form.value[FundOrderBuyProductSelector];
        const product = this.products.find(item => item.code === productCode);

        const amountValue = Helper.getAmountValue(this.orderForm.form.value[FundOrderBuyAmountSelector]);

        let amount: number;
        let quantity: number;

        switch (this.account.AccountSubType) {
            case AccountSubTypeEnum.MutualFund:
                amount = amountValue;
                quantity = 0;
                break;
            case AccountSubTypeEnum.InstrumentCertificate:
                amount = Helper.getAmountValue(this.calculatedAmount);
                quantity = parseInt(amountValue.toString(), 10);
                break;
            default:
                amount = null;
                quantity = null;
                break;
        }

        if (isNaN(amount) || isNaN(quantity)) {
            return null;
        }

        const model: FundOrderBaseInterface = {
            fee: 0,
            interest: 0,
            price: 0,
            cleanPrice: 0,
            accountId: this.account.accountId,
            accountReferenceId: this.account.accountReferenceId,
            orderType: FundOrderTypesEnum.Buy,
            orderPlacementMethod: FundOrderPlacementMethodEnum.Manual,
            productSymbol: product.code,
            productType: product.type,
            amount,
            quantity,
            currency: product.currency,
            accountHolderId: this.account.ownerId,
            accountHolderName: this.account.ownerName,
            createdByUserId: this.operator.user_id,
            createdByUser: [this.operator.first_name, this.operator.last_name].join(' ')
        };

        return model;
    }

    private initFormControls(): void {
        this.amountControl = this.orderForm.form.get(FundOrderBuyAmountSelector);
        this.productControl = this.orderForm.form.get(FundOrderBuyProductSelector);

        this.orderForm.controls.amount.placeholder = GetFundOrderBuyAmountPlaceholderText(this.account.AccountSubType);

        switch (this.account.AccountSubType) {
            case AccountSubTypeEnum.MutualFund:
                this.showProductPriceSection = false;

                break;

            case AccountSubTypeEnum.InstrumentCertificate:
                this.showProductPriceSection = true;
                this.productPriceBoxText = FundOrderMessages.FundOrderBuyProductPriceBoxText.message;

                break;
        }

        if (this.productControl) {
            const subscription = this.productControl.valueChanges.subscribe(value => this.handleProductControlOnChange(value));
            this.orderSubscriptions.push(subscription);
        }

        if (this.amountControl) {
            const subscription = this.amountControl.valueChanges.subscribe(value => this.onValueChanged(value));
            this.orderSubscriptions.push(subscription);
        }

        this.orderForm.controls.product.options = this.initProductsControlOptions(this.products);

        this.setAmountControlValidators();
    }

    private setAmountControlValidators(): void {
        if (this.amountControl) {
            switch (this.account.AccountSubType) {
                case AccountSubTypeEnum.MutualFund:
                    break;
                case AccountSubTypeEnum.InstrumentCertificate:
                    this.amountControl.setValidators([Validators.required]);
                    break;
                default:
                    this.amountControl.setValidators([Validators.required]);
                    break;
            }

            this.amountControl.updateValueAndValidity();
        }
    }

    private initProductsControlOptions(products: FundProductInterface[]): SelectOptionInterface[] {
        return products.map((product: FundProductInterface) => ({
            label: `${product.name} - ${product.code}`,
            value: product.code
        }));
    }

    private handleProductControlOnChange(value: string): void {
        const product = this.products.find(item => item.code === value);

        if (!product) {
            return;
        }

        super.init(product, null, this.data.accountBalance);

        this.manageProductPriceData();
    }

    private manageProductPriceData(): void {
        if (!this.model.product.buyPrice) {
            this.cleanFormData(FundOrderMessages.FundOrderBuyProductPriceBoxNoPricesText.message);

            return;
        }

        this.cleanFormData('');

        this.productCurrencySign = Helper.getCurrencySign(this.model.product.currency);
        this.productPriceAmount = this.model.product.buyPrice.value;
        this.productPriceDate = this.model.product.buyPrice.priceDate;
        this.productPriceBoxText = GetFundOrderBuyPurchaseDateText(this.productPriceDate);
    }

    private cleanFormData(warningBoxText: string): void {
        this.productPriceAmount = 0;
        this.productPriceBoxText = FundOrderMessages.FundOrderBuyProductPriceBoxText.message;
        this.amountNotValidBoxText = '';
        this.amountControl.setValue('');
        this.setProductPriceWarningText(warningBoxText);

        const submitButton = this.config.buttons.find((button: DialogButtonInterface) => button.type === DialogButtonTypesEnum.Confirm);

        if (submitButton) {
            submitButton.disabled = warningBoxText.trim() !== '';
        }
    }

    private setProductPriceWarningText(text: string): void {
        this.productPriceBoxWarningText = text;
    }

    ngOnInit(): void {
        this.initForm();

        this.accountCashCurrencySign = Helper.getCurrencySign(CurrencyTypesEnum.Euro);
        this.productCurrencySign = Helper.getCurrencySign(CurrencyTypesEnum.Euro);
    }

    ngOnDestroy(): void {
        this.orderSubscriptions.forEach((subscription: Subscription) => subscription && subscription.unsubscribe());
    }

    initForm(): void {
        this.orderForm = new FundOrderBuyForm();

        this.orderForm.initForm();

        this.initFormControls();
    }

    getFormMessages(controlLabel?: string): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel);
    }

    validateForm(): boolean {
        return [
            !this.model.invalid,
            (this.amountNotValidBoxText.trim() === ''),
            this.orderForm.validateForm(this.getFormMessages)
        ].indexOf(false) < 0;
    }

    onFormSubmit(action?: number): void {
        switch (action) {
            case DialogActionsEnum.Decline:
                this.dialogRef.close(false);
                break;
            case DialogActionsEnum.ProcessFinished:
                this.dialogRef.close(!this.config.process.failed);
                break;
            case DialogActionsEnum.Confirm:
                if (this.validateForm()) {
                    this.startLoading(() => {
                        this.confirmOrder();

                    });
                }
                break;
        }
    }

    getPrice(): number {
        return this.model.product.buyPrice.value;
    }

    getInterest(): number {
        return this.model.product.buyPrice.accruedInterest;
    }
}

